import { useState } from 'react';
import { motion } from 'framer-motion';
import { Card, Skeleton, useToastActions } from '@rackd/ui';
import { IconShieldCheck, IconSearch, IconMessageCircle } from '@tabler/icons-react';
import { useReviews, useDeleteReview, ReviewCard } from '@/features/moderation';

const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] as const }
  },
};

export function ModerationPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const { reviews, isLoading, error } = useReviews({ search: searchQuery });
  const { deleteReview, isDeleting } = useDeleteReview();
  const toast = useToastActions();

  const handleDeleteReview = async (reviewId: string): Promise<{ success: boolean }> => {
    try {
      await deleteReview(reviewId);
      toast.success('Review deleted', 'The review has been removed');
      return { success: true };
    } catch {
      toast.error('Delete failed', 'Could not delete the review');
      return { success: false };
    }
  };

  // Count reviews that might need attention (low rating or very short)
  const flaggedCount = reviews.filter(
    (r) => r.rating <= 2 || (r.reviewText && r.reviewText.length < 10)
  ).length;

  return (
    <motion.div
      className="space-y-6"
      variants={pageVariants}
      initial="initial"
      animate="animate"
    >
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-[var(--text)]">Review Moderation</h1>
        <p className="text-sm text-[var(--text-tertiary)] mt-1">
          Review and moderate community program reviews ({reviews.length} total
          {flaggedCount > 0 && `, ${flaggedCount} need attention`})
        </p>
      </div>

      {/* Search */}
      <div className="relative">
        <IconSearch
          size={18}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)]"
        />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search reviews by text or program name..."
          className="w-full pl-10 pr-4 py-2.5 bg-[var(--input-bg)] border border-[var(--border)] rounded-lg text-[var(--text)] placeholder:text-[var(--text-faint)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-primary)] focus:border-transparent"
        />
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <Card key={i} className="p-4">
              <div className="flex items-start gap-4">
                <Skeleton className="w-10 h-10 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-48" />
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-3 w-full" />
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : error ? (
        <Card className="p-8 text-center">
          <p className="text-[var(--error)]">Error loading reviews</p>
          <p className="text-sm text-[var(--text-tertiary)] mt-1">{error.message}</p>
        </Card>
      ) : reviews.length === 0 ? (
        <Card className="p-12 text-center">
          <div className="w-16 h-16 bg-[var(--success)]/10 rounded-full flex items-center justify-center mx-auto mb-4">
            {searchQuery ? (
              <IconMessageCircle size={32} className="text-[var(--text-tertiary)]" />
            ) : (
              <IconShieldCheck size={32} className="text-[var(--success)]" />
            )}
          </div>
          <h3 className="text-lg font-semibold text-[var(--text)] mb-1">
            {searchQuery ? 'No reviews found' : 'No reviews yet'}
          </h3>
          <p className="text-sm text-[var(--text-tertiary)]">
            {searchQuery
              ? `No reviews match "${searchQuery}"`
              : 'Reviews will appear here when users rate programs.'}
          </p>
        </Card>
      ) : (
        <div className="space-y-4">
          {reviews.map((review) => (
            <ReviewCard
              key={review.id}
              review={review}
              onDelete={handleDeleteReview}
              isDeleting={isDeleting}
            />
          ))}
        </div>
      )}
    </motion.div>
  );
}
